import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Image,
  Article,
  People,
} from '@mui/icons-material';
import { useSlideImages } from '../hooks/useSlideImages';
import { useNewsItems } from '../hooks/useNewsItems';

const categoryLabels: Record<string, string> = {
  'UPDATE': 'アップデート',
  'EVENT': 'イベント', 
  'MAINTENANCE': 'メンテナンス',
  'ANNOUNCEMENT': 'お知らせ',
};

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: `${color}.main` }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const Dashboard: React.FC = () => {
  const { slideImages } = useSlideImages();
  const { newsItems } = useNewsItems({ limit: 5 });

  const activeSlides = slideImages.filter(slide => slide.isActive).length;
  const publishedNews = newsItems.filter(news => news.isPublished).length;

  const recentNews = newsItems.slice(0, 5);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        ダッシュボード
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <StatCard
            title="スライド画像総数"
            value={slideImages.length}
            icon={<Image fontSize="large" />}
            color="primary"
          />
        </Box>
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <StatCard
            title="アクティブスライド"
            value={activeSlides}
            icon={<TrendingUp fontSize="large" />}
            color="success"
          />
        </Box>
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <StatCard
            title="お知らせ総数"
            value={newsItems.length}
            icon={<Article fontSize="large" />}
            color="secondary"
          />
        </Box>
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <StatCard
            title="公開中のお知らせ"
            value={publishedNews}
            icon={<People fontSize="large" />}
            color="warning"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              最近のお知らせ
            </Typography>
            <List>
              {recentNews.length > 0 ? (
                recentNews.map((news) => (
                  <ListItem key={news.id} divider>
                    <ListItemText
                      primary={news.title}
                      secondary={new Date(news.date).toLocaleDateString('ja-JP')}
                    />
                    <Box sx={{ ml: 2 }}>
                      <Chip
                        label={categoryLabels[news.category] || news.category}
                        size="small"
                        color={news.isPublished ? 'success' : 'default'}
                      />
                    </Box>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="お知らせがありません" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              システム情報
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="サーバー状態"
                  secondary="正常稼働中"
                />
                <Chip label="正常" color="success" size="small" />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="最終更新"
                  secondary={new Date().toLocaleDateString('ja-JP')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="バージョン"
                  secondary="v1.0.0"
                />
              </ListItem>
            </List>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};